import type {
  BatchArgs,
  BatchResponse,
  Folder,
  IDemo,
  Tour,
  TourIntegrationMetadata,
  TourPresentation,
  TourShare,
  Workspace,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/dist/query/react';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import { folderApi, workspaceApi } from '../';
import type { GetSearchLinkResultsArgs } from '../../types';
import { handleBatchRequest } from '../utils';
import {
  queryDataUpdateHandler,
  workspaceAddAsset,
  workspaceRemoveAsset,
} from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:tourApi');

export interface RevertTourArgs extends Pick<Tour, 'id' | 'revisionId'> {
  organizationId: string;
}

export interface RestoreTourArgs extends Pick<Tour, 'id'> {
  organizationId: string;
}

export interface DuplicateTourArgs extends Pick<Tour, 'id'> {
  organizationId: string;
}

export interface UpdateTourDetailsArgs
  extends Pick<Tour, 'id'>,
    Partial<
      Pick<Tour, 'description' | 'tourName' | 'draftSettings' | 'workspaceId'>
    > {
  organizationId: string;
  folderId?: Folder['id'];
}

export interface UpdateTourDraftSettingsArgs {
  organizationId: string;
  id: Tour['id'];
  draftSettings: Partial<Tour['draftSettings']>;
}

export interface GetTourArgs {
  organizationId: string;
  id: number;
  removeDeleted?: boolean;
  workspaceId?: string;
}

export interface DeleteTourArgs extends Pick<Tour, 'id'> {
  organizationId: string;
  folderId?: Folder['id'];
}

export interface GetToursByDemoIdArgs extends BatchArgs {
  organizationId: string;
  demoId: IDemo['id'];
}

export interface GetToursArgs extends BatchArgs {
  organizationId: string;
  workspaces?: string;
}

export interface CopyTourArgs {
  organizationId: string;
  id: Tour['id'];
  tourName?: Tour['tourName'];
  targetWorkspaceId: Workspace['id'];
  folderId?: Folder['id'];
}
export interface MoveTourArgs {
  organizationId: string;
  id: Tour['id'];
  targetWorkspaceId: Workspace['id'];
  workspaceId?: Workspace['id'];
  folderId?: Folder['id'];
  currentFolderId?: Folder['id'];
}

type CreateTourPresentationArgs = {
  organizationId: string;
  tourId: string;
  forClient?: string;
  hidden?: boolean;
  integrationMetadata?: TourIntegrationMetadata;
  notes?: string;
};

type CreateTourPresentationTokenResponse = {
  organizationId: string;
  workspaceId: string;
  token: string;
};

type CreateTourPresentationTokenArgs = {
  organizationId: string;
  tourPresentationId: number;
};

const tourApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    deleteTour: builder.mutation<Tour, DeleteTourArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/tour/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (
        { folderId = '0', ...arg },
        { dispatch, getState, queryFulfilled }
      ) => {
        try {
          const { data: deletedTour } = await queryFulfilled;
          const isDeletingFromFolder = String(folderId) !== '0';
          const state = getState();
          queryDataUpdateHandler({
            api: isDeletingFromFolder ? folderApi : workspaceApi,
            state,
            dispatch,
            endpointName: isDeletingFromFolder
              ? ('getFolderAssets' as QueryKeys<EndpointDefinitions>)
              : ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isDeletingFromFolder
              ? 'folderId'
              : 'workspaceId',
            endpointArgumentValue: isDeletingFromFolder
              ? folderId
              : deletedTour.workspaceId,
            updateQueryDataFunction: (assets) => {
              return workspaceRemoveAsset(assets, arg);
            },
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (result, __, { id, folderId }) =>
        result
          ? [
              { type: 'DemoTours' },
              { type: 'ArchivedTours' },
              { type: 'Tours' },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'FolderAssets', id: folderId },
              { type: 'Tour', id: result.id },
              { type: 'Playbook' },
            ]
          : [
              { type: 'ArchivedTours' },
              { type: 'Tours' },
              { type: 'Tour', id },
            ],
    }),

    getTour: builder.query<Tour, GetTourArgs>({
      query: ({ organizationId, id, workspaceId, removeDeleted = false }) => ({
        url: `/api/${organizationId}/tour/${id}`,
        method: 'GET',
        params: {
          removeDeleted: String(removeDeleted),
          workspaceId,
        },
      }),
      providesTags: (_, __, { id }) => [{ type: 'Tour', id }],
    }),

    updateTourDetails: builder.mutation<Tour, UpdateTourDetailsArgs>({
      query: ({
        organizationId,
        id,
        description,
        tourName,
        draftSettings,
        workspaceId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/tour/${id}`,
        method: 'POST',
        body: {
          description,
          tourName,
          draftSettings,
          ...(workspaceId
            ? {
                workspace: {
                  workspaceId,
                  ...removeUndefinedValues({ folderId }),
                },
              }
            : {}),
        },
      }),
      invalidatesTags: (result, _, { id, folderId }) =>
        result
          ? [
              { type: 'DemoTours' },
              { type: 'Tour', id },
              { type: 'Tours' },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'FolderAssets', id: result.folderId },
              { type: 'Playbook' },
              { type: 'Revisions', id },
            ]
          : [
              { type: 'Tour', id },
              { type: 'Tours' },
              { type: 'FolderAssets', id: folderId },
            ],
      onQueryStarted: async (
        {
          organizationId,
          workspaceId,
          id,
          tourName,
          description,
          draftSettings,
        },
        { dispatch, queryFulfilled }
      ) => {
        if (tourName || draftSettings || description) {
          const patchResult = dispatch(
            tourApi.util.updateQueryData(
              'getTour',
              { organizationId, workspaceId, id },
              (tour) => {
                if (tour) {
                  tour.tourName = tourName || tour.tourName;
                  tour.description = description || tour.description;
                  if (draftSettings) {
                    tour.draftSettings = {
                      ...tour.draftSettings,
                      ...draftSettings,
                    };
                  }
                }
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        }
      },
    }),

    updateTourDraftSettings: builder.mutation<
      Tour,
      UpdateTourDraftSettingsArgs
    >({
      query: ({ id, organizationId, draftSettings }) => ({
        url: `/api/${organizationId}/tour/${id}`,
        method: 'POST',
        body: {
          draftSettings,
        },
      }),
      onQueryStarted: async (
        { id, organizationId, draftSettings },
        { dispatch, queryFulfilled }
      ) => {
        if (draftSettings) {
          const patchResult = dispatch(
            tourApi.util.updateQueryData(
              'getTour',
              { organizationId, id },
              (tour) => {
                if (tour) {
                  if (draftSettings) {
                    tour.draftSettings = {
                      ...tour.draftSettings,
                      ...draftSettings,
                    };
                  }
                }
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        }
      },
    }),

    duplicateTour: builder.mutation<Tour, DuplicateTourArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/tour/${id}/duplicate`,
        method: 'PUT',
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'tour',
        });
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'DemoTours' },
              { type: 'Tours' },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'FolderAssets', id: result.folderId },
            ]
          : [{ type: 'Tours' }],
    }),

    copyTour: builder.mutation<Tour, CopyTourArgs>({
      query: ({
        organizationId,
        id,
        tourName,
        targetWorkspaceId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/tour/${id}/copy`,
        method: 'PUT',
        body: {
          tourName,
          workspaceId: targetWorkspaceId,
          ...removeUndefinedValues({
            folderId,
          }),
        },
      }),
      invalidatesTags: (res, __, { id }) =>
        res
          ? [
              { type: 'Tours' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
              { type: 'Folders', id: res.workspaceId },
              { type: 'Tour', id: res.id },
            ]
          : [{ type: 'Tours' }, { type: 'Tour', id }],
    }),

    moveTour: builder.mutation<Tour, MoveTourArgs>({
      query: ({ organizationId, id, targetWorkspaceId, folderId }) => ({
        url: `/api/${organizationId}/tour/${id}/move`,
        method: 'PUT',
        body: {
          workspaceId: targetWorkspaceId,
          ...removeUndefinedValues({
            folderId,
          }),
        },
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const isMovingFromWorkspaceToFolder =
            arg.folderId !== '0' && arg.currentFolderId === '0';
          const isMovingBetweenWorkspaces =
            arg.folderId === '0' && arg.currentFolderId === '0';
          const shouldInvalidateWorkspaceApi =
            isMovingFromWorkspaceToFolder || isMovingBetweenWorkspaces;
          await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            api: shouldInvalidateWorkspaceApi ? workspaceApi : folderApi,
            state,
            dispatch,
            endpointName: shouldInvalidateWorkspaceApi
              ? ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>)
              : ('getFolderAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: shouldInvalidateWorkspaceApi
              ? 'workspaceId'
              : 'folderId',
            endpointArgumentValue: shouldInvalidateWorkspaceApi
              ? arg.workspaceId
              : arg.currentFolderId,
            updateQueryDataFunction: (assets) =>
              workspaceRemoveAsset(assets, arg),
          });
        } catch (error) {
          console.error(error);
        }
      },
      invalidatesTags: (res, __, arg) =>
        res
          ? [
              { type: 'Tour', id: res.id },
              { type: 'Folders', id: res.workspaceId },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
            ]
          : [{ type: 'Tour', id: arg.id }],
    }),

    restoreTour: builder.mutation<Tour, RestoreTourArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/tour/${id}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restoredTour } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName:
              'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'workspaceId',
            endpointArgumentValue: restoredTour.workspaceId,
            updateQueryDataFunction: (workspaceAssets) =>
              workspaceRemoveAsset(workspaceAssets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'DemoTours' },
              { type: 'Tours' },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
            ]
          : [{ type: 'DemoTours' }, { type: 'Tours' }],
    }),

    getToursByDemoId: builder.query<BatchResponse<Tour>, GetToursByDemoIdArgs>({
      query: ({ organizationId, demoId, ...rest }: GetToursByDemoIdArgs) =>
        handleBatchRequest({
          ...rest,
          url: `/api/${organizationId}/${demoId}/tours`,
        }),
      providesTags: (_, __, { demoId }) => [{ type: 'DemoTours', id: demoId }],
    }),

    getTours: builder.query<BatchResponse<Tour>, GetToursArgs>({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/tours`,
        }),
      providesTags: ['Tours'],
    }),

    getArchivedTours: builder.query<BatchResponse<Tour>, GetToursArgs>({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/tours/archived`,
        }),
      providesTags: ['ArchivedTours'],
    }),

    revertTour: builder.mutation<Tour, RevertTourArgs>({
      query: ({ organizationId, id, revisionId }) => ({
        url: `/api/${organizationId}/tour/${id}/revert`,
        method: 'PUT',
        body: { revisionId },
      }),
      invalidatesTags: (_, __, { id }) => {
        return [{ type: 'Revisions', id }, { type: 'TourFrames' }];
      },
    }),
    getTourSearchLinksResults: builder.query<
      BatchResponse<TourShare>,
      GetSearchLinkResultsArgs
    >({
      query: ({ organizationId, id, creators, workspaces, active, ...rest }) =>
        handleBatchRequest({
          ...rest,
          url: `/api/${organizationId}/share/tour/${id}/search`,
          method: 'POST',
          params: removeUndefinedValues({
            creators: creators?.join(','),
            workspaces: workspaces?.join(','),
            active,
          }),
        }),
      providesTags: [{ type: 'Shares' }],
    }),
    crateTourPresentation: builder.mutation<
      TourPresentation,
      CreateTourPresentationArgs
    >({
      query: ({
        organizationId,
        forClient,
        hidden,
        integrationMetadata,
        notes,
        tourId,
      }) => ({
        url: `/api/${organizationId}/tour-presentation/new`,
        method: 'PUT',
        body: removeUndefinedValues({
          forClient,
          hidden,
          integrationMetadata,
          notes,
          tourId,
        }),
      }),
    }),
    createTourPresentationToken: builder.mutation<
      CreateTourPresentationTokenResponse,
      CreateTourPresentationTokenArgs
    >({
      query: ({ organizationId, tourPresentationId }) => ({
        url: `api/${organizationId}/tour-presentation/${tourPresentationId}/token`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetToursQuery,
  useGetArchivedToursQuery,
  useLazyGetArchivedToursQuery,
  useGetTourQuery,
  useLazyGetTourQuery,
  useLazyGetToursQuery,
  useGetToursByDemoIdQuery,
  useRestoreTourMutation,
  useDeleteTourMutation,
  useUpdateTourDetailsMutation,
  useUpdateTourDraftSettingsMutation,
  useDuplicateTourMutation,
  useRevertTourMutation,
  useCopyTourMutation,
  useMoveTourMutation,
  useGetTourSearchLinksResultsQuery,
  useLazyGetTourSearchLinksResultsQuery,
  useCrateTourPresentationMutation,
  useCreateTourPresentationTokenMutation,
} = tourApi;
